'use client';

import React, { createContext, useEffect, useState, useCallback } from 'react';

/**
 * Theme types supported by the application
 */
export type Theme = 'light' | 'dark';

/**
 * Theme context value type
 */
export interface ThemeContextValue {
  /** Current active theme */
  theme: Theme;
  /** Set theme (with persistence) */
  setTheme: (theme: Theme) => void;
  /** Toggle between light and dark themes */
  toggleTheme: () => void;
  /** Whether the theme is currently being initialized */
  isLoading: boolean;
}

/**
 * Theme context
 */
export const ThemeContext = createContext<ThemeContextValue | undefined>(undefined);

/**
 * Local storage key for theme persistence
 */
const THEME_STORAGE_KEY = 'inspectaone-theme';

/**
 * Get initial theme from localStorage (defaults to light mode)
 * Note: System preference is intentionally not used by default for business applications
 */
function getInitialTheme(): Theme {
  // Check if we're in a browser environment
  if (typeof window === 'undefined') {
    return 'light'; // Default for SSR
  }

  // Try to get theme from localStorage
  const storedTheme = localStorage.getItem(THEME_STORAGE_KEY) as Theme | null;
  if (storedTheme && (storedTheme === 'light' || storedTheme === 'dark')) {
    return storedTheme;
  }

  // Default to light mode for business applications
  return 'light';
}

/**
 * Apply theme to document root
 */
function applyTheme(theme: Theme) {
  if (typeof window === 'undefined') return;

  const root = document.documentElement;
  root.setAttribute('data-theme', theme);

  // Also add a class for Tailwind dark mode support (if using class strategy)
  if (theme === 'dark') {
    root.classList.add('dark');
  } else {
    root.classList.remove('dark');
  }
}

/**
 * ThemeProvider Props
 */
export interface ThemeProviderProps {
  children: React.ReactNode;
  /** Default theme (optional, overrides system preference) */
  defaultTheme?: Theme;
  /** Storage key for theme persistence (optional) */
  storageKey?: string;
}

/**
 * ThemeProvider Component
 *
 * Provides theme context to the entire application.
 * Handles theme state, persistence to localStorage, and system preference detection.
 *
 * @example
 * ```tsx
 * // Wrap your app with ThemeProvider
 * export default function RootLayout({ children }) {
 *   return (
 *     <html lang="en">
 *       <body>
 *         <ThemeProvider>
 *           {children}
 *         </ThemeProvider>
 *       </body>
 *     </html>
 *   );
 * }
 * ```
 */
export function ThemeProvider({
  children,
  defaultTheme,
  storageKey = THEME_STORAGE_KEY,
}: ThemeProviderProps) {
  const [theme, setThemeState] = useState<Theme>('light');
  const [isLoading, setIsLoading] = useState(true);

  // Initialize theme on mount
  useEffect(() => {
    const initialTheme = defaultTheme || getInitialTheme();
    setThemeState(initialTheme);
    applyTheme(initialTheme);
    setIsLoading(false);
  }, [defaultTheme]);

  // Listen for system theme changes
  useEffect(() => {
    if (typeof window === 'undefined') return;

    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');

    const handleChange = (e: MediaQueryListEvent) => {
      // Only update if user hasn't manually set a preference
      const storedTheme = localStorage.getItem(storageKey);
      if (!storedTheme) {
        const newTheme = e.matches ? 'dark' : 'light';
        setThemeState(newTheme);
        applyTheme(newTheme);
      }
    };

    // Modern browsers
    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener('change', handleChange);
      return () => mediaQuery.removeEventListener('change', handleChange);
    }
    // Older browsers
    else if (mediaQuery.addListener) {
      mediaQuery.addListener(handleChange);
      return () => mediaQuery.removeListener(handleChange);
    }
    return undefined;
  }, [storageKey]);

  /**
   * Set theme with persistence
   */
  const setTheme = useCallback(
    (newTheme: Theme) => {
      setThemeState(newTheme);
      applyTheme(newTheme);

      // Persist to localStorage
      if (typeof window !== 'undefined') {
        localStorage.setItem(storageKey, newTheme);
      }
    },
    [storageKey]
  );

  /**
   * Toggle between light and dark themes
   */
  const toggleTheme = useCallback(() => {
    setTheme(theme === 'light' ? 'dark' : 'light');
  }, [theme, setTheme]);

  const value: ThemeContextValue = {
    theme,
    setTheme,
    toggleTheme,
    isLoading,
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
}

/**
 * Script to prevent flash of unstyled content (FOUC)
 * This should be added to the <head> of your document before any content renders
 *
 * @example
 * ```tsx
 * // In app/layout.tsx or pages/_document.tsx
 * export default function RootLayout({ children }) {
 *   return (
 *     <html lang="en">
 *       <head>
 *         <ThemeScript />
 *       </head>
 *       <body>
 *         <ThemeProvider>{children}</ThemeProvider>
 *       </body>
 *     </html>
 *   );
 * }
 * ```
 */
export function ThemeScript({ nonce }: { nonce?: string }) {
  const themeScript = `
    (function() {
      const storageKey = '${THEME_STORAGE_KEY}';
      const storedTheme = localStorage.getItem(storageKey);
      const theme = storedTheme || 'light';

      document.documentElement.setAttribute('data-theme', theme);
      if (theme === 'dark') {
        document.documentElement.classList.add('dark');
      }
    })();
  `;

  return (
    <script
      nonce={nonce}
      dangerouslySetInnerHTML={{ __html: themeScript }}
      suppressHydrationWarning
    />
  );
}
